import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useRouter, useRouterState } from "@tanstack/react-router"

import { useLeaveGuard, type LeaveGuard, type UseLeaveGuardOptions } from "./application-leave-guard"
import { useApplicationRouteState, type ApplicationRouteState } from "./application-router"
import { useDocumentInstanceLifetime, useDocumentInstanceServices } from "./document-instance"
import type { ApplicationScope } from "./plugin-runtime"
import { useApplication } from "./application-provider"
import { createBrowserStorage, createMemoryStorage } from "../storage/storage"
import { DocumentViewState } from "../view-state/document-view-state"

export interface RoutedDocumentLocation {
  readonly pathname: string
  readonly href: string
  readonly routeId: string | null
  readonly params: Readonly<Record<string, string>>
  readonly search: unknown
  readonly loaderData: unknown
}

export interface RoutedDocument<TDocument> {
  readonly id: string
  readonly key: string
  readonly version: unknown
  readonly document: TDocument
  readonly location: string
  readonly scope: ApplicationScope | null
  /** Last successfully resolved route snapshot, independent of the active router location. */
  readonly routeLocation: RoutedDocumentLocation | null
}

export interface OpenDocumentOptions {
  /** Ordinary opens reuse a matching instance. New opens always create fresh local state. */
  readonly mode?: "reuse" | "new"
  /** Insert a new instance beside this tab. Otherwise append it. */
  readonly afterDocumentId?: string
}

declare module "@tanstack/react-router" {
  interface HistoryState {
    __florisoftDocumentInstance?: string
  }
}

export type RoutedDocumentNavigationMode = "replace-active" | "open"

export interface RoutedDocumentsOptions<TDocument> {
  /**
   * `multiple` (default) retains several documents. `single` holds at most one: navigating to another document
   * replaces the current one after its leave guards agree, and nothing is restored from session storage.
   */
  readonly mode?: "multiple" | "single"
  /** Restore tab identities, order and URLs within this browser tab. Use a workspace/account-specific key. */
  readonly sessionStorageKey?: string
  readonly initialDocuments: readonly TDocument[]
  readonly fallbackDocument: TDocument
  readonly getDocumentLocation: (document: TDocument) => string
  /** Stable logical identity. Defaults to the concrete document location. */
  readonly getDocumentKey?: (document: TDocument) => string
  /** Optional semantic version used to refresh retained document descriptors at the same URL. */
  readonly getDocumentVersion?: (document: TDocument) => unknown
  /** Route-driven navigation may replace the active tab or open another retained instance. */
  readonly getDocumentNavigationMode?: (document: TDocument) => RoutedDocumentNavigationMode
  /** Allows leave guards to distinguish retained-tab navigation from destructive navigation. */
  readonly getRouteNavigationMode?: (routeId: string | null) => RoutedDocumentNavigationMode | null
  readonly resolveDocument: (location: RoutedDocumentLocation) => TDocument | null
}

export interface RoutedDocuments<TDocument> {
  readonly viewState: DocumentViewState
  readonly documents: readonly RoutedDocument<TDocument>[]
  readonly activeDocument: RoutedDocument<TDocument> | null
  readonly routeState: ApplicationRouteState
  /**
   * Whether the router is loading a location of the active document, such as a move of its page stack. The active
   * document can stay shown meanwhile, instead of the loading state of a navigation to another document.
   */
  readonly navigatingWithinActiveDocument: boolean
  activateDocument(id: string): void
  openDocument(document: TDocument, options?: OpenDocumentOptions): void
  /**
   * Replaces the descriptor of an open document, for example after its page moved to another URL. The tab keeps its
   * identity and local state; the active tab follows to the new URL.
   */
  replaceDocument(id: string, document: TDocument): void
  /** Update a tab's URL state. Only the active tab writes to browser history, using replace. */
  setDocumentUrlValue(documentId: string, fieldId: string, value: string | undefined): void
  /**
   * Attempts to close one document. Resolves `false` when the document is not
   * closable or one of its registered leave guards rejects the close.
   * An approved close owns any route change needed to activate the remaining
   * document, so router leave blockers are not invoked a second time.
   */
  closeDocument(id: string): Promise<boolean>
  /** Low-level registration used by `useDocumentLeaveGuard`. */
  registerLeaveGuard(id: string, guard: LeaveGuard, options?: DocumentLeaveGuardOptions): () => void
  moveDocument(sourceId: string, targetId: string, position: "before" | "after"): void
  retryNavigation(): void
  returnToActiveDocument(): void
}

export interface DocumentLeaveGuardOptions {
  readonly enableBeforeUnload?: boolean
}

interface RegisteredLeaveGuard {
  readonly guard: LeaveGuard
  readonly enableBeforeUnload: boolean
}

interface PendingDocumentNavigation {
  readonly id: string
  readonly location: string
  readonly created: boolean
  readonly routeLocation: RoutedDocumentLocation | null
}

function createDocument<TDocument>(
  document: TDocument,
  id: string,
  getDocumentLocation: RoutedDocumentsOptions<TDocument>["getDocumentLocation"],
  getDocumentKey: (document: TDocument) => string,
  getDocumentVersion: (document: TDocument) => unknown
): RoutedDocument<TDocument> {
  return {
    id,
    key: getDocumentKey(document),
    version: getDocumentVersion(document),
    document,
    location: getDocumentLocation(document),
    scope: null,
    routeLocation: null,
  }
}

export function useRoutedDocuments<TDocument>(options: RoutedDocumentsOptions<TDocument>): RoutedDocuments<TDocument> {
  const navigate = useNavigate()
  const router = useRouter()
  const application = useApplication()
  const [viewState] = useState(() => {
    const storageKey =
      options.sessionStorageKey == null ? "document-view-state" : `${options.sessionStorageKey}:view-state`
    try {
      return new DocumentViewState(
        application.viewState,
        options.sessionStorageKey == null ? createMemoryStorage() : createBrowserStorage({ area: "session" }),
        storageKey
      )
    } catch {
      return new DocumentViewState(application.viewState, createMemoryStorage(), storageKey)
    }
  })
  const routeState = useApplicationRouteState()
  const location = useRouterState({
    select: (state) => {
      const match = state.matches[state.matches.length - 1]
      return {
        pathname: state.location.pathname.replace(/\/$/, "") || "/",
        href: state.location.href,
        routeId: match?.routeId ?? null,
        params: (match?.params ?? {}) as Readonly<Record<string, string>>,
        search: match?.search ?? state.location.search,
        loaderData: match?.loaderData,
        instanceId: state.location.state.__florisoftDocumentInstance,
      }
    },
  })
  const nextDocumentIdRef = useRef(0)
  const [instancePrefix] = useState(() => crypto.getRandomValues(new Uint32Array(4)).join("-"))
  const getDocumentKey = (document: TDocument) =>
    options.getDocumentKey?.(document) ?? options.getDocumentLocation(document)
  const getDocumentVersion = (document: TDocument) => options.getDocumentVersion?.(document)
  const createDocumentInstance = (document: TDocument) =>
    createDocument(
      document,
      `${instancePrefix}-document-${++nextDocumentIdRef.current}`,
      options.getDocumentLocation,
      getDocumentKey,
      getDocumentVersion
    )
  const single = options.mode === "single"
  const [restoredWorkspace] = useState(() => {
    const initial = {
      documents: (single ? options.initialDocuments.slice(0, 1) : options.initialDocuments).map(createDocumentInstance),
      activeId: null as string | null,
    }
    if (options.sessionStorageKey == null || single) return initial
    try {
      const value: unknown = JSON.parse(sessionStorage.getItem(options.sessionStorageKey) ?? "null")
      if (value == null || typeof value !== "object") return initial
      const snapshot = value as { version?: unknown; documents?: unknown; activeId?: unknown }
      if (snapshot.version !== 1 || !Array.isArray(snapshot.documents)) return initial
      const ids = new Set<string>()
      const restored = snapshot.documents.flatMap((entry: unknown): RoutedDocument<TDocument>[] => {
        if (entry == null || typeof entry !== "object") return []
        const { id, location: href } = entry as { id?: unknown; location?: unknown }
        if (typeof id !== "string" || !id || ids.has(id) || typeof href !== "string") return []
        // Only local router URLs are restorable. Never deserialize scopes or loader data.
        if (!href.startsWith("/") || href.startsWith("//") || href.includes("\\")) return []
        try {
          const url = new URL(href, "http://workspace.local")
          const search = router.options.parseSearch!(url.search)
          const matches = router.matchRoutes(url.pathname, search)
          const match = matches[matches.length - 1]
          if (
            match == null ||
            matches.some(
              (candidate) =>
                candidate._notFound ||
                candidate.error != null ||
                candidate.searchError != null ||
                candidate.paramsError != null
            )
          )
            return []
          const routeLocation: RoutedDocumentLocation = {
            pathname: url.pathname.replace(/\/$/, "") || "/",
            href,
            routeId: match.routeId,
            params: match.params as Readonly<Record<string, string>>,
            search: match.search,
            loaderData: undefined,
          }
          const document = options.resolveDocument(routeLocation)
          if (document == null) return []
          ids.add(id)
          return [
            {
              ...createDocument(document, id, options.getDocumentLocation, getDocumentKey, getDocumentVersion),
              routeLocation,
            },
          ]
        } catch {
          return []
        }
      })
      if (restored.length === 0) return initial
      const savedActive = restored.find((document) => document.id === snapshot.activeId)
      return { documents: restored, activeId: savedActive?.location === location.href ? savedActive.id : null }
    } catch {
      // Disabled storage or a damaged snapshot must not prevent navigation.
      return initial
    }
  })
  const [documents, setDocuments] = useState<RoutedDocument<TDocument>[]>(restoredWorkspace.documents)
  const documentsRef = useRef(documents)
  documentsRef.current = documents
  useEffect(() => {
    let cancelled = false
    void viewState.whenReady().then(() => {
      if (!cancelled) viewState.retainInstances(new Set(documents.map((document) => document.id)))
    })
    return () => {
      cancelled = true
    }
  }, [documents, viewState])
  const lastSuccessfulDocumentIdRef = useRef(restoredWorkspace.activeId ?? documents[0]?.id ?? null)
  const pendingNavigationRef = useRef<PendingDocumentNavigation | null>(null)
  const leaveGuardsRef = useRef(new Map<string, Set<RegisteredLeaveGuard>>())
  const pendingCloseRef = useRef(new Map<string, Promise<boolean>>())
  const pendingNavigationLeaveRef = useRef<Promise<boolean> | null>(null)
  const [leaveGuardRevision, setLeaveGuardRevision] = useState(0)

  const currentDocument = useMemo(
    () => (routeState.status === "ready" ? options.resolveDocument(location) : null),
    [location, options, routeState.status]
  )
  const currentDocumentKey = currentDocument == null ? null : getDocumentKey(currentDocument)
  const lastActiveDocument = documents.find((document) => document.id === lastSuccessfulDocumentIdRef.current)
  const activeDocument =
    (currentDocumentKey == null
      ? null
      : (documents.find((document) => document.id === location.instanceId && document.key === currentDocumentKey) ??
        (lastActiveDocument?.key === currentDocumentKey ? lastActiveDocument : null) ??
        documents.find((document) => document.key === currentDocumentKey))) ??
    lastActiveDocument ??
    documents[0] ??
    null
  // The key of the location the router is still loading, matched without its loaders. A move within one document, such
  // as one of its page stack, loads a location of the document that is already shown.
  const loadingDocumentKey = useMemo(() => {
    if (routeState.status !== "loading") return null
    const { pathname, search } = router.state.location
    const matches = router.matchRoutes(pathname, search)
    const match = matches[matches.length - 1]
    if (match == null) return null
    const document = options.resolveDocument({
      pathname: pathname.replace(/\/$/, "") || "/",
      href: location.href,
      routeId: match.routeId,
      params: match.params as Readonly<Record<string, string>>,
      search: match.search,
      loaderData: undefined,
    })
    return document == null ? null : getDocumentKey(document)
  }, [routeState.status, location.href])
  const navigatingWithinActiveDocument =
    activeDocument !== null &&
    loadingDocumentKey === activeDocument.key &&
    (location.instanceId === undefined || location.instanceId === activeDocument.id)
  const activeDocumentRef = useRef(activeDocument)
  activeDocumentRef.current = activeDocument

  const activeLeaveGuardState = useMemo(() => {
    const registrations = activeDocument == null ? undefined : leaveGuardsRef.current.get(activeDocument.id)
    return {
      hasActiveGuards: (registrations?.size ?? 0) > 0,
      enableBeforeUnload: [...leaveGuardsRef.current.values()].some((documentRegistrations) =>
        [...documentRegistrations].some((registration) => registration.enableBeforeUnload)
      ),
    }
  }, [activeDocument, leaveGuardRevision])
  const guardsActiveNavigation = routeState.status === "ready" && activeLeaveGuardState.hasActiveGuards
  const guardsActiveNavigationRef = useRef(guardsActiveNavigation)
  guardsActiveNavigationRef.current = guardsActiveNavigation

  const registerLeaveGuard = useCallback(
    (id: string, guard: LeaveGuard, guardOptions: DocumentLeaveGuardOptions = {}) => {
      const registration: RegisteredLeaveGuard = {
        guard,
        enableBeforeUnload: guardOptions.enableBeforeUnload ?? false,
      }
      let registrations = leaveGuardsRef.current.get(id)
      if (registrations == null) {
        registrations = new Set()
        leaveGuardsRef.current.set(id, registrations)
      }
      registrations.add(registration)
      setLeaveGuardRevision((revision) => revision + 1)

      return () => {
        registrations.delete(registration)
        if (registrations.size === 0 && leaveGuardsRef.current.get(id) === registrations) {
          leaveGuardsRef.current.delete(id)
        }
        setLeaveGuardRevision((revision) => revision + 1)
      }
    },
    []
  )

  const canLeaveDocument = useCallback(async (id: string) => {
    const registrations = [...(leaveGuardsRef.current.get(id) ?? [])]
    for (const registration of registrations) {
      if (!(await registration.guard())) return false
    }
    return true
  }, [])

  // Sign-out discards every open document, so it asks the active document first, then the others in tab order.
  useEffect(
    () =>
      application.registerSignOutGuard(async () => {
        const activeId = activeDocumentRef.current?.id
        const ids = [
          ...(activeId == null ? [] : [activeId]),
          ...documentsRef.current.map((document) => document.id).filter((id) => id !== activeId),
        ]
        for (const id of ids) {
          if (leaveGuardsRef.current.has(id) && !(await canLeaveDocument(id))) return false
        }
        return true
      }),
    [application, canLeaveDocument]
  )

  const cancelPendingNavigation = useCallback((pendingNavigation: PendingDocumentNavigation | null) => {
    if (pendingNavigationRef.current === pendingNavigation) {
      pendingNavigationRef.current = null
    }
    if (pendingNavigation?.created) {
      setDocuments((currentDocuments) => currentDocuments.filter((document) => document.id !== pendingNavigation.id))
    }
  }, [])

  const canLeaveActiveDocument = useCallback(async () => {
    if (!guardsActiveNavigationRef.current) return true

    const id = activeDocumentRef.current?.id
    if (id == null) return true

    const pendingNavigation = pendingNavigationRef.current
    let decision = pendingNavigationLeaveRef.current
    if (decision == null) {
      const pendingDecision = canLeaveDocument(id)
      const trackedDecision = pendingDecision.then(
        (allowed) => {
          if (pendingNavigationLeaveRef.current === trackedDecision) {
            pendingNavigationLeaveRef.current = null
          }
          return allowed
        },
        (error) => {
          if (pendingNavigationLeaveRef.current === trackedDecision) {
            pendingNavigationLeaveRef.current = null
          }
          throw error
        }
      )
      pendingNavigationLeaveRef.current = trackedDecision
      decision = trackedDecision
    }

    const allowed = await decision
    if (!allowed) cancelPendingNavigation(pendingNavigation)
    return allowed
  }, [canLeaveDocument, cancelPendingNavigation])

  useLeaveGuard(canLeaveActiveDocument, {
    enabled: guardsActiveNavigation || activeLeaveGuardState.enableBeforeUnload,
    enableBeforeUnload: activeLeaveGuardState.enableBeforeUnload,
    shouldGuardNavigation: (navigation) =>
      single || options.getRouteNavigationMode?.(navigation.next.routeId) !== "open",
  })

  useLayoutEffect(() => {
    if (routeState.status !== "ready" || currentDocument == null) return

    const currentLocation = options.getDocumentLocation(currentDocument)
    const currentKey = getDocumentKey(currentDocument)
    const currentVersion = getDocumentVersion(currentDocument)
    const pendingNavigation = pendingNavigationRef.current
    const retainActiveInstance = (id: string) => {
      lastSuccessfulDocumentIdRef.current = id
      if (location.instanceId !== id) {
        // The router location is internal. Its browser URL also includes basepath and configured URL rewrites.
        router.history.replace(router.latestLocation.publicHref, {
          ...router.state.location.state,
          __florisoftDocumentInstance: id,
        })
      }
    }

    if (pendingNavigation?.location === currentLocation && pendingNavigation.id === location.instanceId) {
      pendingNavigationRef.current = null
      setDocuments((currentDocuments) =>
        currentDocuments.map((document) =>
          document.id === pendingNavigation.id
            ? {
                ...document,
                key: currentKey,
                version: currentVersion,
                document: currentDocument,
                location: currentLocation,
                scope: routeState.scope,
                routeLocation: location,
              }
            : document
        )
      )
      retainActiveInstance(pendingNavigation.id)
      return
    }

    const openDocument = activeDocument?.key === currentKey ? activeDocument : null
    if (openDocument != null) {
      if (
        openDocument.scope == null ||
        openDocument.scope.id !== routeState.scope.id ||
        openDocument.location !== currentLocation ||
        !Object.is(openDocument.version, currentVersion)
      ) {
        setDocuments((currentDocuments) =>
          currentDocuments.map((document) =>
            document.id === openDocument.id
              ? {
                  ...document,
                  key: currentKey,
                  version: currentVersion,
                  document: currentDocument,
                  location: currentLocation,
                  scope: routeState.scope,
                  routeLocation: location,
                }
              : document
          )
        )
      }
      retainActiveInstance(openDocument.id)
      return
    }

    if (single || options.getDocumentNavigationMode?.(currentDocument) === "open") {
      const nextDocument = {
        ...createDocumentInstance(currentDocument),
        scope: routeState.scope,
        routeLocation: location,
      }
      setDocuments((currentDocuments) => (single ? [nextDocument] : [...currentDocuments, nextDocument]))
      retainActiveInstance(nextDocument.id)
      return
    }

    if (activeDocument == null) {
      const nextDocument = {
        ...createDocumentInstance(currentDocument),
        scope: routeState.scope,
        routeLocation: location,
      }
      setDocuments([nextDocument])
      retainActiveInstance(nextDocument.id)
      return
    }

    retainActiveInstance(activeDocument.id)
    setDocuments((currentDocuments) =>
      currentDocuments.map((document) =>
        document.id === activeDocument.id
          ? {
              ...document,
              key: currentKey,
              version: currentVersion,
              document: currentDocument,
              location: currentLocation,
              scope: routeState.scope,
              routeLocation: location,
            }
          : document
      )
    )
  }, [activeDocument, currentDocument, documents, location, options, routeState, router])

  useEffect(() => {
    if (
      options.sessionStorageKey == null ||
      single ||
      routeState.status !== "ready" ||
      pendingNavigationRef.current != null
    )
      return
    try {
      sessionStorage.setItem(
        options.sessionStorageKey,
        JSON.stringify({
          version: 1,
          activeId: lastSuccessfulDocumentIdRef.current,
          documents: documents.map(({ id, location: href }) => ({ id, location: href })),
        })
      )
    } catch {
      // Session persistence is optional when browser storage is unavailable or full.
    }
  }, [
    activeDocument?.id,
    documents,
    location.href,
    location.instanceId,
    options.sessionStorageKey,
    routeState.status,
    single,
  ])

  const navigateToDocument = useCallback(
    (document: RoutedDocument<TDocument>, created: boolean, ignoreBlocker = false, replace = false) => {
      pendingNavigationRef.current = {
        id: document.id,
        location: document.location,
        created,
        routeLocation: document.routeLocation,
      }
      void navigate({
        to: document.location,
        ignoreBlocker,
        replace,
        state: (previous) => ({ ...previous, __florisoftDocumentInstance: document.id }),
      })
    },
    [navigate]
  )

  const activateDocument = useCallback(
    (id: string) => {
      const document = documents.find((candidate) => candidate.id === id)
      if (document == null || document.id === activeDocumentRef.current?.id) return
      navigateToDocument(document, false)
    },
    [documents, navigateToDocument]
  )

  const replaceDocument = useCallback(
    (id: string, document: TDocument) => {
      const existing = documentsRef.current.find((candidate) => candidate.id === id)
      if (existing == null) return
      const updated = {
        ...existing,
        key: options.getDocumentKey?.(document) ?? options.getDocumentLocation(document),
        version: options.getDocumentVersion?.(document),
        document,
        location: options.getDocumentLocation(document),
      }
      setDocuments((currentDocuments) =>
        currentDocuments.map((candidate) => (candidate.id === id ? updated : candidate))
      )
      if (activeDocumentRef.current?.id === id && updated.location !== existing.location) {
        navigateToDocument(updated, false, true, true)
      }
    },
    [navigateToDocument, options]
  )

  const openDocument = useCallback(
    (document: TDocument, openOptions: OpenDocumentOptions = {}) => {
      const key = getDocumentKey(document)
      const version = getDocumentVersion(document)
      const location = options.getDocumentLocation(document)
      const currentDocuments = documentsRef.current
      const active = activeDocumentRef.current
      const existing =
        openOptions.mode === "new"
          ? null
          : active?.key === key
            ? active
            : currentDocuments.find((candidate) => candidate.key === key)
      if (existing != null) {
        const updated = {
          ...existing,
          key,
          version,
          document,
          location,
        }
        if (
          updated.document !== existing.document ||
          updated.location !== existing.location ||
          !Object.is(updated.version, existing.version)
        ) {
          setDocuments((currentDocuments) =>
            currentDocuments.map((candidate) => (candidate.id === existing.id ? updated : candidate))
          )
        }
        navigateToDocument(updated, false)
        return
      }

      if (single) {
        // The router location decides; the layout effect swaps the document in once the route is ready.
        void navigate({ to: location })
        return
      }

      const nextDocument = createDocumentInstance(document)
      setDocuments((currentDocuments) => {
        const afterIndex = currentDocuments.findIndex((candidate) => candidate.id === openOptions.afterDocumentId)
        const next = [...currentDocuments]
        next.splice(afterIndex < 0 ? next.length : afterIndex + 1, 0, nextDocument)
        return next
      })
      navigateToDocument(nextDocument, true)
    },
    [documents, navigate, navigateToDocument, options, single]
  )

  const setDocumentUrlValue = useCallback(
    (documentId: string, fieldId: string, value: string | undefined) => {
      const document = documentsRef.current.find((candidate) => candidate.id === documentId)
      if (document?.routeLocation == null) return
      const pending = pendingNavigationRef.current
      const sourceLocation =
        pending?.id === documentId ? (pending.routeLocation ?? document.routeLocation) : document.routeLocation
      const search = { ...(sourceLocation.search as Record<string, unknown>) }
      if (value === undefined) delete search[fieldId]
      else search[fieldId] = value
      const nextLocation = router.buildLocation({
        to: sourceLocation.pathname,
        search: () => search,
        hash: document.location.split("#")[1] ?? "",
      })
      if (nextLocation.href === sourceLocation.href) return
      const routeLocation = { ...sourceLocation, href: nextLocation.href, search: nextLocation.search }
      const descriptor = options.resolveDocument(routeLocation)
      if (descriptor == null) return
      const updated = {
        ...document,
        document: descriptor,
        key: getDocumentKey(descriptor),
        version: getDocumentVersion(descriptor),
        location: nextLocation.href,
        routeLocation,
      }
      if (activeDocumentRef.current?.id === documentId) {
        navigateToDocument(updated, false, false, true)
      } else {
        const nextDocuments = documentsRef.current.map((candidate) =>
          candidate.id === documentId ? updated : candidate
        )
        documentsRef.current = nextDocuments
        setDocuments(nextDocuments)
      }
    },
    [navigateToDocument, options, router]
  )

  const closeDocument = useCallback(
    (id: string): Promise<boolean> => {
      const pending = pendingCloseRef.current.get(id)
      if (pending != null) return pending

      const closeOperation = (async () => {
        let currentDocuments = documentsRef.current
        let closedIndex = currentDocuments.findIndex((document) => document.id === id)
        if (closedIndex < 0) return false

        const closed = currentDocuments[closedIndex]
        if (currentDocuments.length === 1 && closed.key === getDocumentKey(options.fallbackDocument)) {
          return false
        }

        if (!(await canLeaveDocument(id))) return false

        // The guard may have been asynchronous. Re-resolve against the latest
        // document state before applying the close.
        currentDocuments = documentsRef.current
        closedIndex = currentDocuments.findIndex((document) => document.id === id)
        if (closedIndex < 0) return false

        if (leaveGuardsRef.current.delete(id)) {
          setLeaveGuardRevision((revision) => revision + 1)
        }

        if (currentDocuments.length === 1) {
          const fallback = createDocumentInstance(options.fallbackDocument)
          setDocuments([fallback])
          // Closing is a distinct operation with its own guard. Once approved,
          // the controller owns the resulting route change and bypasses route
          // blockers so the document list and URL cannot diverge.
          navigateToDocument(fallback, false, true)
          return true
        }

        const remaining = currentDocuments.filter((document) => document.id !== id)
        setDocuments(remaining)
        if (pendingNavigationRef.current?.id === id) {
          pendingNavigationRef.current = null
          const returnDocument =
            remaining.find((document) => document.id === lastSuccessfulDocumentIdRef.current) ??
            remaining[closedIndex - 1] ??
            remaining[0]
          if (returnDocument != null) {
            lastSuccessfulDocumentIdRef.current = returnDocument.id
            navigateToDocument(returnDocument, false, true)
          }
          return true
        }

        if (activeDocumentRef.current?.id !== id) return true

        const nextDocument = remaining[closedIndex - 1] ?? remaining[0]
        lastSuccessfulDocumentIdRef.current = nextDocument.id
        navigateToDocument(nextDocument, false, true)
        return true
      })()

      const trackedOperation = closeOperation.then(
        (result) => {
          if (pendingCloseRef.current.get(id) === trackedOperation) pendingCloseRef.current.delete(id)
          return result
        },
        (error) => {
          if (pendingCloseRef.current.get(id) === trackedOperation) pendingCloseRef.current.delete(id)
          throw error
        }
      )
      pendingCloseRef.current.set(id, trackedOperation)
      return trackedOperation
    },
    [canLeaveDocument, navigateToDocument, options]
  )

  const moveDocument = useCallback((sourceId: string, targetId: string, position: "before" | "after") => {
    setDocuments((currentDocuments) => {
      const sourceIndex = currentDocuments.findIndex((document) => document.id === sourceId)
      const targetIndex = currentDocuments.findIndex((document) => document.id === targetId)
      if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return currentDocuments

      const reordered = [...currentDocuments]
      const [sourceDocument] = reordered.splice(sourceIndex, 1)
      let insertionIndex = targetIndex + (position === "after" ? 1 : 0)
      if (sourceIndex < insertionIndex) insertionIndex -= 1
      reordered.splice(insertionIndex, 0, sourceDocument)
      return reordered
    })
  }, [])

  const retryNavigation = useCallback(() => {
    void router.invalidate()
  }, [router])

  const returnToActiveDocument = useCallback(() => {
    const pendingNavigation = pendingNavigationRef.current
    pendingNavigationRef.current = null
    if (pendingNavigation?.created) {
      setDocuments((currentDocuments) => currentDocuments.filter((document) => document.id !== pendingNavigation.id))
    }

    const returnDocument =
      documents.find((document) => document.id === lastSuccessfulDocumentIdRef.current) ??
      documents.find((document) => document.scope != null) ??
      documents[0]
    if (returnDocument != null) {
      navigateToDocument(returnDocument, false)
    }
  }, [documents, navigateToDocument])

  return {
    documents,
    viewState,
    activeDocument,
    routeState,
    navigatingWithinActiveDocument,
    activateDocument,
    openDocument,
    replaceDocument,
    setDocumentUrlValue,
    closeDocument,
    registerLeaveGuard,
    moveDocument,
    retryNavigation,
    returnToActiveDocument,
  }
}

/**
 * Registers one consumer-owned guard for both route navigation away from the
 * active document and explicit document close. Inactive retained documents do
 * not block unrelated navigation, but their guards still protect tab close.
 */
export function useDocumentLeaveGuard(guard: LeaveGuard, options?: UseLeaveGuardOptions): void
/** @deprecated Hosted documents can register against their instance context directly. */
export function useDocumentLeaveGuard<TDocument>(
  workspace: RoutedDocuments<TDocument>,
  documentId: string,
  guard: LeaveGuard,
  options?: UseLeaveGuardOptions
): void
export function useDocumentLeaveGuard<TDocument>(
  workspaceOrGuard: RoutedDocuments<TDocument> | LeaveGuard,
  documentIdOrOptions?: string | UseLeaveGuardOptions,
  explicitGuard?: LeaveGuard,
  explicitOptions: UseLeaveGuardOptions = {}
) {
  const documentLifetime = useDocumentInstanceLifetime()
  const instanceServices = useDocumentInstanceServices()
  const contextMode = typeof workspaceOrGuard === "function"
  const workspace = contextMode ? null : workspaceOrGuard
  const documentId = contextMode ? null : (documentIdOrOptions as string)
  const guard = contextMode ? workspaceOrGuard : explicitGuard!
  const options = contextMode ? ((documentIdOrOptions as UseLeaveGuardOptions | undefined) ?? {}) : explicitOptions
  const enabled = options.enabled ?? true
  if (contextMode && enabled && instanceServices == null) {
    throw new Error("useDocumentLeaveGuard must be used inside a hosted document instance.")
  }
  const guardRef = useRef(guard)
  guardRef.current = guard
  const registeredGuard = useCallback(() => guardRef.current(), [])
  const instanceRegisterLeaveGuard = instanceServices?.registerLeaveGuard
  const explicitRegisterLeaveGuard = workspace?.registerLeaveGuard
  const canRegister = contextMode
    ? instanceRegisterLeaveGuard != null
    : explicitRegisterLeaveGuard != null && documentId != null
  const registerLeaveGuard = useCallback(
    (nextGuard: LeaveGuard, nextOptions?: DocumentLeaveGuardOptions) => {
      if (contextMode) {
        return instanceRegisterLeaveGuard?.(nextGuard, nextOptions) ?? (() => undefined)
      }
      if (explicitRegisterLeaveGuard == null || documentId == null) return () => undefined
      return explicitRegisterLeaveGuard(documentId, nextGuard, nextOptions)
    },
    [contextMode, documentId, explicitRegisterLeaveGuard, instanceRegisterLeaveGuard]
  )
  const registrationIdentity = useMemo(
    () => ({
      enableBeforeUnload: options.enableBeforeUnload,
      registerLeaveGuard,
    }),
    [options.enableBeforeUnload, registerLeaveGuard]
  )
  const registrationRef = useRef<{
    readonly identity: typeof registrationIdentity
    readonly dispose: () => void
  } | null>(null)

  useEffect(() => {
    let registration = registrationRef.current
    if (registration != null && (registration.identity !== registrationIdentity || !enabled)) {
      documentLifetime?.release(registration)
      registration.dispose()
      registrationRef.current = null
      registration = null
    }

    if (!enabled || !canRegister) return

    if (registration == null) {
      registration = {
        identity: registrationIdentity,
        dispose: registerLeaveGuard(registeredGuard, {
          enableBeforeUnload: options.enableBeforeUnload,
        }),
      }
      registrationRef.current = registration
      documentLifetime?.retain(registration, registration.dispose)
    }

    return () => {
      if (documentLifetime != null) {
        if (!documentLifetime.releaseFromEffect(registration)) return
      } else {
        registration.dispose()
      }
      if (registrationRef.current === registration) registrationRef.current = null
    }
  }, [
    canRegister,
    documentLifetime,
    enabled,
    options.enableBeforeUnload,
    registerLeaveGuard,
    registeredGuard,
    registrationIdentity,
  ])
}
